import React, { useState } from "react";

import type { Column, RowData } from "@tanstack/react-table-v9";
import styled from "styled-components";

import { colorTheme, gridSpacing } from "@utils";

import { TriStateCheckbox } from "./SelectionCells";
import TableV2IconButton from "./TableV2IconButton";
import Button from "../../Button/Button";
import Popover from "../../Popover/Popover";
import SearchText from "../../SearchText/SearchText";
import {
  canReorderColumn,
  getColumnLabel,
  getLeafColumnOrder,
  isUtilityColumn,
  moveColumn,
  nudgeColumn,
} from "../columnUtils";
import type { TableV2Features } from "../features";
import type { TableV2Instance } from "../hooks/useTableV2";

type AnyColumn<TData extends RowData> = Column<TableV2Features, TData, unknown>;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gridSpacing[2]}px;
  width: 280px;
  max-height: 420px;
  padding: ${gridSpacing[3]}px;
  border: 1px solid ${colorTheme("neutralL4")};
  border-radius: 5px;
  background: ${colorTheme("white")};
  box-shadow: 0 4px 12px ${colorTheme("neutralL4")};
`;

const SearchRow = styled.div`
  flex-shrink: 0;
`;

const List = styled.ul`
  flex: 1;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
`;

const SectionTitle = styled.li`
  padding: ${gridSpacing[2]}px ${gridSpacing[1]}px ${gridSpacing[1]}px;
  font-size: 12px;
  font-weight: bold;
  color: ${colorTheme("neutralL1")};
  text-transform: uppercase;
`;

const Item = styled.li<{
  $dropIndicator?: "before" | "after";
  $dragging?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${gridSpacing[2]}px;
  padding: ${gridSpacing[1]}px;
  border-radius: 4px;
  opacity: ${({ $dragging }) => ($dragging ? 0.5 : 1)};
  box-shadow: ${({ $dropIndicator }) =>
    $dropIndicator
      ? `inset 0 ${$dropIndicator === "before" ? "2px" : "-2px"} 0 ${colorTheme("primary")}`
      : "none"};

  &:hover {
    background: ${colorTheme("neutralL5")};
  }
`;

const Handle = styled.span<{ $disabled: boolean }>`
  color: ${colorTheme("neutralL2")};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "grab")};
  visibility: ${({ $disabled }) => ($disabled ? "hidden" : "visible")};
`;

const Label = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${gridSpacing[2]}px;
  border-top: 1px solid ${colorTheme("neutralL4")};
`;

type DropTarget = { id: string; placement: "before" | "after" } | null;

const ColumnPicker = <TData extends RowData>({
  table,
}: {
  table: TableV2Instance<TData>;
}) => {
  const [search, setSearch] = useState("");
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget>(null);

  const query = search.trim().toLowerCase();
  const columns = getLeafColumnOrder(table)
    .map((id) => table.getColumn(id))
    .filter(
      (column): column is AnyColumn<TData> =>
        !!column && !isUtilityColumn(column),
    )
    .filter(
      (column) =>
        !query || getColumnLabel(column).toLowerCase().includes(query),
    );
  // List columns in the order the table shows them: pinned left, unpinned, pinned right
  const region = (column: AnyColumn<TData>) => {
    const pinned = column.getIsPinned();
    return pinned === "start" ? 0 : pinned === "end" ? 2 : 1;
  };
  columns.sort((a, b) => region(a) - region(b));
  // Pinned columns are placed by pinning, so only unpinned ones reorder here
  const isMovable = (column: AnyColumn<TData>) =>
    canReorderColumn(column) && !column.getIsPinned();
  const movable = columns.filter(isMovable);
  const dragEnabled = !query;

  const clearDrag = () => {
    setDraggingId(null);
    setDropTarget(null);
  };

  const rows: React.ReactNode[] = [];
  let lastParentId: string | undefined;
  let lastRegion = -1;
  columns.forEach((column) => {
    const columnRegion = region(column);
    if (
      columnRegion !== lastRegion &&
      (columnRegion !== 1 || lastRegion === 0)
    ) {
      const title = ["Pinned left", "Columns", "Pinned right"][columnRegion];
      rows.push(
        <SectionTitle key={`region-${columnRegion}`}>{title}</SectionTitle>,
      );
      lastParentId = undefined;
    }
    lastRegion = columnRegion;

    const parentId = column.parent?.id;
    if (parentId !== lastParentId && column.parent) {
      rows.push(
        <SectionTitle key={`section-${parentId}-${column.id}`}>
          {getColumnLabel(column.parent)}
        </SectionTitle>,
      );
    }
    lastParentId = parentId;

    const label = getColumnLabel(column);
    const canHide = column.getCanHide();
    const canMove = dragEnabled && isMovable(column);
    const movableIndex = movable.indexOf(column);

    rows.push(
      <Item
        key={column.id}
        data-testid={`column-picker-item-${column.id}`}
        draggable={canMove}
        $dragging={draggingId === column.id}
        $dropIndicator={
          dropTarget?.id === column.id && draggingId !== column.id
            ? dropTarget.placement
            : undefined
        }
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", column.id);
          setDraggingId(column.id);
        }}
        onDragOver={(event) => {
          if (!draggingId || draggingId === column.id || !canMove) return;
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          const placement =
            event.clientY < rect.top + rect.height / 2 ? "before" : "after";
          if (
            dropTarget?.id !== column.id ||
            dropTarget.placement !== placement
          ) {
            setDropTarget({ id: column.id, placement });
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          if (draggingId && dropTarget)
            moveColumn(table, draggingId, column.id, dropTarget.placement);
          clearDrag();
        }}
        onDragEnd={clearDrag}
      >
        <Handle $disabled={!canMove}>
          <i className="fa-solid fa-grip-vertical" />
        </Handle>
        <TriStateCheckbox
          checked={column.getIsVisible()}
          disabled={!canHide}
          label={`Show ${label}`}
          onClick={
            canHide
              ? () => column.toggleVisibility(!column.getIsVisible())
              : undefined
          }
        />
        <Label>{label}</Label>
        {dragEnabled && isMovable(column) && (
          <>
            <TableV2IconButton
              tooltip={false}
              icon="fa-solid fa-chevron-up"
              label={`Move ${label} up`}
              hidden={movableIndex <= 0}
              disabled={movableIndex <= 0}
              onClick={() => nudgeColumn(table, column.id, -1)}
            />
            <TableV2IconButton
              tooltip={false}
              icon="fa-solid fa-chevron-down"
              label={`Move ${label} down`}
              hidden={movableIndex === movable.length - 1}
              disabled={movableIndex === movable.length - 1}
              onClick={() => nudgeColumn(table, column.id, 1)}
            />
          </>
        )}
      </Item>,
    );
  });

  return (
    <Popover>
      <Popover.Trigger asChild>
        <span>
          <TableV2IconButton
            icon="fa-solid fa-table-columns"
            label="Choose columns"
            onClick={() => undefined}
          />
        </span>
      </Popover.Trigger>
      <Popover.Content align="end" sideOffset={4}>
        <Panel role="dialog" data-testid="column-picker">
          <SearchRow>
            <SearchText
              fixedWidth
              style={{ width: "100%", boxSizing: "border-box" }}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </SearchRow>
          <List>
            {rows.length > 0 ? rows : <Item as="li">No matching columns</Item>}
          </List>
          <Footer>
            <Button
              small
              variant="link"
              text="Show all"
              onClick={() => table.toggleAllColumnsVisible(true)}
            />
            <Button
              small
              variant="link"
              text="Reset"
              onClick={() => {
                if (table.resetColumns) return table.resetColumns();
                table.resetColumnVisibility();
                table.resetColumnOrder();
              }}
            />
          </Footer>
        </Panel>
      </Popover.Content>
    </Popover>
  );
};

export default ColumnPicker;
